/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { FileUploader } from "react-drag-drop-files";
import html2canvas from "html2canvas";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  VStack,
} from "../components/utils";
import convertToBase64 from "../utils/convertToBase64";
import { Input, InputWithLabel } from "../components/shared/InputField";
import { useCompanyStore } from "../store";
import { useSignaturePadDialog } from "../store/useDialogStore";

export type SignatureProps = {
  onClose: () => void;
  ids?: string[];
  type?: string;
};

const SignatureGateway = (props: SignatureProps) => {
  const { onClose, ids, type } = props;
  const [step, setStep] = useState(2);
  const [mode, setMode] = useState("");
  const { companyData } = useCompanyStore();
  const handleModeChange = (newMode: string) => {
    setMode(newMode);
  };
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();
  const grantLetterSettings = companyData?.companySettings.grantLetterSettings;
  function getSignature() {
    setShowSignaturePad({ open: true, signatureType: "esign" });
  }
  return (
    <Box className="w-full h-full">
      <HStack className="justify-between p-4 px-6 mt-3 text-lg font-medium border-b stone-700">
        {mode === "" && (
          <h6 className="justify-start">
            How would you like to Sign
            <p className="text-gray-401 text-sm">
              Select a method to provide your signature for approving this
              grant.
            </p>
          </h6>
        )}
        {mode === "draw" && (
          <h6 className="justify-start">
            Add your Signature
            <p className="text-gray-401 text-sm">
              Draw your signature directly using your mouse or touchpad.
              <br />
              If you already have a scanned image of your signature, you can
              upload it instead.
            </p>
          </h6>
        )}
        {mode === "type" && (
          <h6 className="justify-start">
            Create Your Signature
            <p className="text-gray-401 text-sm">
              Type your full name. Choose the signature that feels most like
              your own.
            </p>
          </h6>
        )}
        <span className="cursor-pointer justify-end" onClick={onClose}>
          X
        </span>
      </HStack>
      {step === 0 && (
        <HStack className="h-full m-4 border border-collapse border-solid justify-evenly">
          <HStack className="gap-4 p-4 border hover:border-orange-501">
            <VStack className="items-center justify-between gap-4">
              <VStack>
                <li className="list-none">
                  Inscribing the signature can be done via 3 Modes:-
                </li>
                <li className="list-none">
                  Drag and Drop any signature Image.
                </li>
                <li className="list-none">
                  Draw your signature on Embedded Signature pad.
                </li>
                <li className="list-none">
                  Generate Signature by entering your name.
                </li>
              </VStack>
              <VStack>
                <ButtonPrimary onClick={() => setStep(2)} className="w-fit">
                  Inscribe Signature
                </ButtonPrimary>
              </VStack>
            </VStack>
          </HStack>
        </HStack>
      )}
      {step === 2 && <SignaturePad handleModeChange={handleModeChange} />}
    </Box>
  );
};
const SignaturePad = (props: { handleModeChange: (val: string) => void }) => {
  const { handleModeChange } = props;
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();
  const [mode, setMode] = useState("");
  const [activeMode, setActiveMode] = useState<
    "draw" | "upload" | "type" | null
  >(null);
  const [checkDraw, setCheckDraw] = useState(false);
  const canvasRef = useRef<SignatureCanvas>(null);
  const [uploadedImage, setUploadedImage] = useState("");
  const [generatedSignature, setGeneratedSignature] = useState("");
  const canSave = useMemo(() => {
    if (mode === "draw") return !(checkDraw || uploadedImage);
    if (mode === "upload") return !uploadedImage;
    if (mode === "type") return !generatedSignature.trim();
    return true;
  }, [checkDraw, uploadedImage, generatedSignature, mode]);

  const [uploaderKey, setUploaderKey] = useState(0);
  useEffect(() => {
    handleModeChange(mode);
  }, [mode]);
  const resetSignatureGeneratorRef = useRef<() => void>();
  function getUploadedImage(signature: string) {
    setUploadedImage(signature);
    setShowSignaturePad({ ...showSignaturePad, signature });
    if (!activeMode) setActiveMode("upload");
  }
  const handleClearTypedSignature = () => {
    resetSignatureGeneratorRef.current?.();
  };
  return (
    <Box className="h-100">
      {mode === "" && (
        <>
          <Box className="p-10 rounded-xl bg-white shadow-md">
            <HStack className="h-full mt-4 gap-4 justify-evenly">
              <VStack>
                <Box className="w-full h-60">
                  <Box
                    onClick={() => setMode("draw")}
                    className="w-56 h-56 p-4 text-center align-middle cursor-pointer hover:border-orange-501 hover:border-4 hover:border-solid text-slate-501 justify-middle"
                  >
                    <div className="p-4 text-base underline">
                      Draw Signature
                    </div>
                    <div className="flex justify-center">
                      <Icon
                        className="cursor-pointer text-orange-501"
                        icon="material-symbols:draw"
                        width={36}
                      />
                    </div>
                    <div className="mt-2">
                      Go artistic. Use your mouse or trackpad to make a
                      statement.
                    </div>
                  </Box>
                </Box>
              </VStack>
              <VStack>
                <Box className="w-full h-60">
                  <Box
                    onClick={() => setMode("type")}
                    className="w-56 h-56 p-4 text-center align-middle cursor-pointer hover:border-orange-501 hover:border-4 hover:border-solid text-slate-501 justify-middle"
                  >
                    <div className="p-4 text-base underline">
                      Type Signature
                    </div>
                    <div className="flex justify-center">
                      <Icon
                        className="cursor-pointer text-orange-501"
                        icon="tabler:keyboard"
                        width={36}
                      />
                    </div>
                    <div>
                      Type your name and choose the font that best matches your
                      personality.
                    </div>
                  </Box>
                </Box>
              </VStack>
            </HStack>
          </Box>
        </>
      )}
      {mode === "draw" && (
        <HStack className="gap-4 p-6 px-8">
          <VStack>
            <HStack className="justify-center pt-10">
              Draw your Signature Here
            </HStack>
            <HStack
              className={`border-2 border-solid ${
                activeMode === "upload"
                  ? "pointer-events-none opacity-50"
                  : "bg-slate-300"
              }`}
            >
              <SignatureCanvas
                penColor="black"
                ref={canvasRef}
                onBegin={() => {
                  if (!activeMode) setActiveMode("draw");
                }}
                onEnd={() => {
                  if (canvasRef.current && !canvasRef.current.isEmpty()) {
                    setCheckDraw(true);
                  }
                }}
                canvasProps={{
                  width: "300px",
                  height: "200px",
                  className: "bg-slate-300",
                }}
                backgroundColor="rgb(255, 255, 255)"
              />
            </HStack>
          </VStack>
          <VStack className="relative w-8">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
              OR
            </div>
          </VStack>
          <VStack className="">
            <HStack className="justify-center pt-10">
              Upload your Signature Here
            </HStack>
            <HStack className="mt-6">
              <div
                className={`${
                  activeMode === "draw" ? "pointer-events-none opacity-50" : ""
                }`}
              >
                <DragDrop
                  onImageUpload={getUploadedImage}
                  labelKey={uploaderKey}
                />
              </div>
            </HStack>
            {uploadedImage && (
              <HStack className="items-center px-4 mx-auto mt-6 border border-solid rounded-md">
                <img
                  src={`data:image/png;base64,${uploadedImage}`}
                  alt=""
                  id="signatureImage"
                />
              </HStack>
            )}
          </VStack>
        </HStack>
      )}
      {mode === "type" && (
        <HStack className="w-full p-6">
          <SignatureGenerator
            selectedSignaure={(signature) => {
              setGeneratedSignature(signature);
              if (!activeMode) setActiveMode("type");
            }}
            setResetRef={(fn) => {
              resetSignatureGeneratorRef.current = fn;
            }}
          />
        </HStack>
      )}
      {mode !== "" && (
        <HStack className="w-full p-6 justify-between">
          <ButtonPrimary1
            className="m-2 flex items-start"
            onClick={() => {
              setMode("");
              setActiveMode(null);
            }}
          >
            Back
          </ButtonPrimary1>
          <HStack className="justify-end">
            <div className="m-2">
              <ButtonPrimary
                disabled={canSave}
                onClick={() => {
                  if (mode === "draw") {
                    if (canvasRef?.current?.isEmpty() && uploadedImage === "") {
                      setShowSignaturePad({
                        open: true,
                        signature: "",
                        signatureType: "error",
                      });
                      toast("Draw/upload your signature", {
                        autoClose: 2000,
                        type: "warning",
                      });
                    }
                    if (uploadedImage !== "") {
                      setShowSignaturePad({
                        ...showSignaturePad,
                        signatureType: "regular",
                      });
                    }
                    if (!canvasRef?.current?.isEmpty()) {
                      setShowSignaturePad({
                        ...showSignaturePad,
                        signature:
                          canvasRef?.current?.getTrimmedCanvas().toDataURL() ||
                          "",
                        signatureType: "regular",
                      });
                    }
                  } else if (mode === "type") {
                    if (
                      !generatedSignature ||
                      generatedSignature.trim() === ""
                    ) {
                      toast("Please select a signature before saving", {
                        autoClose: 2000,
                        type: "warning",
                      });
                      setShowSignaturePad({
                        open: true,
                        signature: "",
                        signatureType: "error",
                      });
                      return;
                    }

                    setShowSignaturePad({
                      ...showSignaturePad,
                      signature: generatedSignature,
                      signatureType: "regular",
                    });
                  }
                }}
              >
                {!showSignaturePad.isLoading ? (
                  `Save`
                ) : (
                  <>
                    Please Wait...
                    <Icon
                      className="animate-spin"
                      icon="lucide:loader-2"
                      width={18}
                    />
                  </>
                )}
              </ButtonPrimary>
            </div>
            {mode !== "type" && (
              <ButtonSecondary
                className="m-2"
                onClick={() => {
                  canvasRef?.current?.clear();
                  setCheckDraw(false);
                  setUploadedImage("");
                  setActiveMode(null);
                  setUploaderKey((prev) => prev + 1);
                }}
              >
                Clear
              </ButtonSecondary>
            )}
            {mode === "type" && (
              <ButtonSecondary
                className="m-2"
                onClick={() => {
                  handleClearTypedSignature();
                  setGeneratedSignature("");
                  setActiveMode(null);
                }}
              >
                Clear
              </ButtonSecondary>
            )}
          </HStack>
        </HStack>
      )}
    </Box>
  );
};

function DragDrop(props: {
  onImageUpload: (sign: string) => void;
  labelKey: number;
}) {
  const { onImageUpload, labelKey } = props;
  const [file, setFile] = useState("");
  const [uploaderKey, setUploaderKey] = useState(labelKey);
  const [uploadMessage, setUploadMessage] = useState("");
  const fileTypes = ["JPG", "JPEG", "PNG"];
  useEffect(() => {
    if (labelKey !== undefined) {
      setUploaderKey(labelKey);
    }
  }, [labelKey]);

  const isValidImageFileUploaded = (file: File) => {
    const validExtensions = ["png", "jpeg", "jpg"];
    const fileExtension = file.type.split("/")[1];
    if (!validExtensions.includes(fileExtension)) {
      return false;
    }
    if (file.size / 1024 > 50) {
      toast(
        "File size should be less than 50kb, please upload a different file",
        {
          type: "error",
        }
      );
      return false;
    }
    return true;
  };
  const handleChange = async (file: any) => {
    if (file && file.length > 0) {
      const fileToCheck = file[0];
      if (!isValidImageFileUploaded(fileToCheck)) {
        setUploaderKey((prev) => prev + 1);
        return;
      }
      const img = new Image();
      img.src = URL.createObjectURL(fileToCheck);
      img.onload = async () => {
        const width = img.width;
        const height = img.height;
        if (width > 240 || height > 120) {
          toast(
            "File height and width should be less than 120px and 240px, please upload a different file",
            {
              type: "error",
            }
          );
          setUploaderKey((prev) => prev + 1);
        } else {
          convertToBase64(file[0]).then((data: any) => {
            setFile(data as string);
            onImageUpload(data as unknown as string);
          });
        }
      };
    }
  };
  return (
    <div className="">
      <FileUploader
        key={uploaderKey}
        handleChange={handleChange}
        uploadedLabel="Uploaded succesfully"
        label="Upload or drop a file"
        multiple="false"
        name="file"
        types={fileTypes}
      />
    </div>
  );
}

const SignatureGenerator = (props: {
  selectedSignaure: (signature: string) => void;
  setResetRef: (fn: () => void) => void;
}) => {
  const setResetRef = props.setResetRef;
  const [name, setName] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState("");
  const [hasGenerated, setHasGenerated] = useState(false);
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();

  useEffect(() => {
    const userName = showSignaturePad.userName;
    if (userName) {
      setName(userName);
    }
  }, [showSignaturePad.userName, hasGenerated]);

  useEffect(() => {
    if (setResetRef) {
      setResetRef(() => {
        const userName = showSignaturePad.userName ?? "";
        setName(userName);
        setSelectedSignature("");
        setSignatures([]);
        setHasGenerated(false);
      });
    }
  }, [setResetRef]);

  const onSignatureSelect = (signtaure: string) => {
    setSelectedSignature(signtaure);
    setShowSignaturePad({ open: true, signature: signtaure || "" });
    props.selectedSignaure(signtaure);
  };
  const generateSignatureStyles = (customName?: string) => {
    const displayName = customName ?? name;
    const generatedSignatures = [];

    for (let i = 0; i < 10; i++) {
      const style = {
        fontFamily: [
          "cursive",
          "Great Vibes",
          "Nothing You Could Do",
          "serif",
          "Marck Script",
        ][Math.floor(Math.random() * 5)],
        color: ["black", "blue"][Math.floor(Math.random() * 2)],
        textTransform: ["uppercase", "lowercase", "capitalize"][
          Math.floor(Math.random() * 3)
        ],
        fontSize: "18px",
      };
      generatedSignatures.push({ name: displayName, style });
    }

    setSignatures(generatedSignatures as []);
  };

  return (
    <HStack className="w-full min-w-[600px] h-64 mt-8 mb-4 justify-evenly gap-x-8">
      <VStack>
        <HStack>
          <Input
            type="text"
            placeholder="Enter Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </HStack>
        <HStack className="mt-4">
          <ButtonPrimary
            onClick={() => {
              if (!name.trim()) {
                toast("Please enter your name before generating signatures", {
                  type: "warning",
                  autoClose: 2000,
                });
                return;
              }
              generateSignatureStyles(name);
              setHasGenerated(true);
            }}
          >
            {signatures.length > 0 ? "Generate More" : "Generate Signatures"}
          </ButtonPrimary>
        </HStack>
        {selectedSignature && (
          <HStack className="mt-8 w-10">
            <table>
              <tbody>
                <tr className="">Selected Signature</tr>
                <tr>
                  {selectedSignature && (
                    <img className="max-w-xs mt-4" src={selectedSignature} />
                  )}
                </tr>
              </tbody>
            </table>
          </HStack>
        )}
      </VStack>
      <VStack className="w-1/2 overflow-y-auto">
        <table>
          <thead>
            <tr>{hasGenerated && <td>Generated Signatures:</td>}</tr>
          </thead>
          <tbody>
            {signatures.length > 0 &&
              signatures.map((signature: { style: {}; name: any }, index) => (
                <tr key={index} style={signature?.style}>
                  <td key={index} style={signature?.style}>
                    <TextToImage
                      onSignatureSelect={onSignatureSelect}
                      style={signature.style}
                      text={signature.name}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </VStack>
    </HStack>
  );
};
const TextToImage = (props: {
  text: string;
  style: any;
  onSignatureSelect: any;
}) => {
  const { text, style, onSignatureSelect } = props;
  const imageRef = useRef<any>();

  const convertToImage = () => {
    imageRef.current.style.backgroundColor = "transparent";
    html2canvas(imageRef.current as HTMLElement).then((canvas) => {
      // Convert the canvas to a data URL
      const image = canvas.toDataURL();
      onSignatureSelect(image);
    });
  };

  return (
    <div
      onClick={convertToImage}
      className="p-4 m-4 cursor-pointer min-h-fit"
      ref={imageRef}
      style={style}
    >
      {text}
    </div>
  );
};

export default SignatureGateway;
